import { useEffect, useCallback, useRef } from "react"
import { useBlocker } from "react-router"
import { Dialog } from "@sembark-travel/ui/dialog"
import { Button, Inline, Stack, Text } from "@sembark-travel/ui/base"

/**
 * Hook to prompt user before navigating away from a page to prevent accidental navigations.
 * @returns Object containing methods to control blocking behavior.
 */
export function useNavigationPrompt(when = true) {
	// Use ref instead of state to avoid re-render delay - critical for disablePrompt() to work
	// immediately before navigation (e.g., after successful form save)
	const shouldBlockRef = useRef(when)

	// React Router's useBlocker manages its own state and triggers re-renders when blocker.state
	// changes. Our callback is called on each navigation attempt, reading the fresh ref value.
	const blocker = useBlocker(() => shouldBlockRef.current)

	// Handle browser navigation (refresh, close tab, etc.)
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (!shouldBlockRef.current) return

			event.preventDefault()
			event.returnValue = "" // Chrome requires returnValue to be set
			return "" // Some browsers need a return value
		}

		window.addEventListener("beforeunload", handleBeforeUnload)

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload)
		}
	}, [])

	useEffect(() => {
		shouldBlockRef.current = when
	}, [when])

	const disablePrompt = useCallback(() => {
		shouldBlockRef.current = false
	}, [])

	const enablePrompt = useCallback(() => {
		shouldBlockRef.current = true
	}, [])

	return {
		blocker,
		disablePrompt,
		enablePrompt,
		isBlocking: shouldBlockRef.current,
	}
}

/**
 * Navigation Prompt Dialog Component - Shows a confirmation dialog when user tries to navigate away.
 */
export function NavigationPromptDialog({
	blocker,
}: {
	blocker: ReturnType<typeof useBlocker>
}) {
	const isBlocked = blocker.state === "blocked"

	const handleStay = () => {
		blocker.reset?.()
	}

	const handleLeave = () => {
		if (blocker.state === "blocked") {
			blocker.proceed?.()
		}
	}

	return (
		<Dialog open={isBlocked} onClose={handleStay} title="Unsaved Changes" sm>
			<Dialog.Body>
				<Stack gap="4">
					<Text>
						You have unsaved changes in the quote. Are you sure you want to
						leave? All changes will be lost.
					</Text>
					<Inline gap="2" justifyContent="end">
						<Button status="primary" onClick={handleStay}>
							Stay on Page
						</Button>
						<Button status="danger" onClick={handleLeave}>
							Leave Page
						</Button>
					</Inline>
				</Stack>
			</Dialog.Body>
		</Dialog>
	)
}
